/**
 * PdfDownloadButton.jsx
 *
 * A plug-and-play PDF download button component.
 * Drop it into any Manage page — it handles everything internally.
 *
 * Usage:
 *   <PdfDownloadButton
 *     title="Customer Listing Report"
 *     subtitle="Status: Active"
 *     fileName="Customer_List"
 *     fetchData={getCsvListData}        ← pass your existing data fetcher
 *     columns={PDF_COLUMNS}             ← define once per page (see below)
 *     summary={summaryCards}            ← optional
 *   />
 *
 * Column definition:
 *   { header: "Client ID", key: "clientno", width: 28 }
 *   { header: "Status",    key: "status",   width: 26, type: "status" }
 *   { header: "Amount",    key: "amount",   width: 34, type: "currency" }
 *   { header: "#",         key: null,       width: 10, type: "index" }
 */

import { useState } from "react";
import { Button } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";

// ─── Brand config ─────────────────────────────────────────────────────────────
// Reads REACT_APP_INSTANCE from .env at build time.
// Add a new entry here when onboarding a new banking client.
// import montranLogo from "../assets/logos/montran-logo.png";
import ubaLogo from "../assets/images/finsys_771.png";
// import mtnLogo from "../assets/logos/mtn-momo-logo.png";
import mtnLogo from "../assets/images/finsys_771.png";
import montranLogo from "../assets/images/Logo-Montran2.png";

const BRAND_MAP = {
  montran: {
    companyName: "Montran Corporation",
    logo: montranLogo,
    primary: [0, 168, 150], // #00A896
    secondary: [230, 248, 246], // E6F8F6
    headerText: [255, 255, 255],
  },
  uba: {
    companyName: "United Bank for Africa",
    logo: ubaLogo,
    primary: [190, 0, 0],
    secondary: [253, 235, 235],
    headerText: [255, 255, 255],
  },
  mtn_momo: {
    companyName: "MTN Mobile Money",
    logo: mtnLogo,
    primary: [255, 204, 0],
    secondary: [255, 249, 219],
    headerText: [30, 30, 30], // dark text on yellow
  },
};

const BRAND = BRAND_MAP[process.env.REACT_APP_INSTANCE] ?? BRAND_MAP.montran;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function loadJsPDF() {
  return new Promise((resolve, reject) => {
    if (window.jspdf?.jsPDF) return resolve(window.jspdf.jsPDF);
    if (document.querySelector('script[src*="jspdf"]')) {
      const poll = setInterval(() => {
        if (window.jspdf?.jsPDF) {
          clearInterval(poll);
          resolve(window.jspdf.jsPDF);
        }
      }, 100);
      return;
    }
    const s = document.createElement("script");
    s.src =
      "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s.onload = () => resolve(window.jspdf.jsPDF);
    s.onerror = () => reject(new Error("Failed to load jsPDF"));
    document.head.appendChild(s);
  });
}

function imageToBase64(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function statusPillColors(status = "") {
  switch (status.toLowerCase()) {
    case "active":
    case "approved":
    case "completed":
      return { bg: [240, 255, 244], text: [0, 110, 0] };
    case "closed":
    case "rejected":
    case "failed":
      return { bg: [255, 242, 240], text: [150, 0, 0] };
    default:
      return { bg: [255, 251, 230], text: [124, 81, 0] };
  }
}

// ─── Core PDF generator (internal — not exported) ─────────────────────────────
async function buildPDF({ title, subtitle, columns, data, fileName, summary }) {
  const [JsPDF, logoBase64] = await Promise.all([
    loadJsPDF(),
    imageToBase64(BRAND.logo),
  ]);

  const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const now = new Date().toLocaleString();

  const MARGIN = 10;
  const ROW_H = 8;
  const PAD = 3;
  const FOOTER_H = 10;
  const HEADER_H = 24;
  const SUMMARY_H = summary?.length ? 16 : 0;
  const COL_H = ROW_H;
  const FIRST_Y = HEADER_H + SUMMARY_H + COL_H;
  const CONT_Y = 14 + COL_H;

  // Scale columns to fit page width
  const totalW = columns.reduce((s, c) => s + (c.width ?? 30), 0);
  const scale = totalW > pageW - MARGIN * 2 ? (pageW - MARGIN * 2) / totalW : 1;

  // ── Draw page header ────────────────────────────────────────────────────
  function drawHeader(isFirst) {
    const h = isFirst ? HEADER_H : 14;
    doc.setFillColor(...BRAND.primary);
    doc.rect(0, 0, pageW, h, "F");

    // Logo — constrained to a small tasteful size in the top-left
    if (logoBase64 && isFirst) {
      try {
        // Width: 22mm, Height: 10mm — adjust per logo aspect ratio if needed
        doc.addImage(logoBase64, "PNG", MARGIN, 5, 22, 10, undefined, "FAST");
      } catch (_) {}
    }

    // Text starts just after the logo (logo width 22 + margin 10 + gap 4 = 36)
    const textX = logoBase64 && isFirst ? MARGIN + 26 : MARGIN;

    doc.setTextColor(...BRAND.headerText);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(isFirst ? 13 : 10);
    doc.text(title.toUpperCase(), textX, isFirst ? 11 : 8);

    if (subtitle && isFirst) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(subtitle, textX, 18);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(BRAND.companyName, pageW - MARGIN, isFirst ? 9 : 6, {
      align: "right",
    });
    doc.text(`Generated: ${now}`, pageW - MARGIN, isFirst ? 15 : 11, {
      align: "right",
    });
  }

  // ── Summary strip ───────────────────────────────────────────────────────
  function drawSummary(y) {
    if (!summary?.length) return;
    doc.setFillColor(...BRAND.secondary);
    doc.rect(0, y, pageW, SUMMARY_H, "F");
    const itemW = (pageW - MARGIN * 2) / summary.length;
    summary.forEach((item, i) => {
      const x = MARGIN + i * itemW;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text(String(item.label).toUpperCase(), x, y + 5);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...BRAND.primary);
      doc.text(String(item.value), x, y + 13);
    });
  }

  // ── Column headers ──────────────────────────────────────────────────────
  function drawColHeaders(y) {
    doc.setFillColor(...BRAND.primary);
    doc.rect(0, y, pageW, COL_H, "F");
    doc.setTextColor(...BRAND.headerText);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    let x = MARGIN;
    columns.forEach((col) => {
      doc.text(col.header, x + PAD, y + 5.5);
      x += (col.width ?? 30) * scale;
    });
  }

  // ── Footer ──────────────────────────────────────────────────────────────
  function drawFooters() {
    const total = doc.internal.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
      doc.setPage(p);
      doc.setFillColor(...BRAND.primary);
      doc.rect(0, pageH - FOOTER_H, pageW, FOOTER_H, "F");
      doc.setTextColor(...BRAND.headerText);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.text("CONFIDENTIAL — For authorised use only", MARGIN, pageH - 4);
      doc.text(
        `${BRAND.companyName}  |  Page ${p} of ${total}`,
        pageW - MARGIN,
        pageH - 4,
        { align: "right" },
      );
    }
  }

  // ── First page ──────────────────────────────────────────────────────────
  drawHeader(true);
  drawSummary(HEADER_H);
  drawColHeaders(HEADER_H + SUMMARY_H);

  let y = FIRST_Y;

  // ── Rows ────────────────────────────────────────────────────────────────
  data.forEach((row, idx) => {
    if (y + ROW_H > pageH - FOOTER_H - 2) {
      doc.addPage();
      drawHeader(false);
      drawColHeaders(14);
      y = CONT_Y;
    }

    // Alternating row background
    if (idx % 2 === 0) {
      doc.setFillColor(...BRAND.secondary);
      doc.rect(0, y, pageW, ROW_H, "F");
    }

    // Row border
    doc.setDrawColor(220, 225, 232);
    doc.setLineWidth(0.2);
    doc.line(MARGIN, y + ROW_H, pageW - MARGIN, y + ROW_H);

    let x = MARGIN;
    columns.forEach((col) => {
      const w = (col.width ?? 30) * scale;
      const type = col.type ?? "text";
      const val = String(row[col.key] ?? "-");

      if (type === "index") {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(160, 160, 160);
        doc.text(String(idx + 1), x + PAD, y + 5.5);
      } else if (type === "status") {
        const { bg, text } = statusPillColors(val);
        const pillW = Math.min(w - PAD * 2, 26);
        doc.setFillColor(...bg);
        doc.roundedRect(x + PAD, y + 1.8, pillW, 4.5, 1.5, 1.5, "F");
        doc.setTextColor(...text);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.text(val, x + PAD + pillW / 2, y + 5.2, { align: "center" });
      } else if (type === "currency") {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(40, 40, 40);
        const formatted = Number(row[col.key] ?? 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        doc.text(formatted, x + w - PAD, y + 5.5, { align: "right" });
      } else {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(40, 40, 40);
        const maxChars = Math.floor(w / 2);
        const display =
          val.length > maxChars ? val.slice(0, maxChars - 1) + "…" : val;
        doc.text(display, x + PAD, y + 5.5);
      }

      x += w;
    });

    y += ROW_H;
  });

  drawFooters();
  doc.save(`${fileName}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ─── The Component ────────────────────────────────────────────────────────────
/**
 * PdfDownloadButton
 *
 * @prop {string}   title      Report title shown in PDF header
 * @prop {string}   subtitle   Optional subtitle (e.g. "Status: Active")
 * @prop {string}   fileName   Base file name (date appended automatically)
 * @prop {function} fetchData  Async function that resolves to the full data array
 * @prop {Array}    columns    Column definitions [{ header, key, width, type? }]
 * @prop {Array}    summary    Optional summary cards [{ label, value }]
 * @prop {object}   btnProps   Any extra Ant Design Button props (size, style, etc.)
 */
const PdfDownloadButton = ({
  title = "Report",
  subtitle = "",
  fileName = "Report",
  fetchData,
  columns = [],
  summary = [],
  btnProps = {},
}) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    try {
      setLoading(true);
      // fetchData is the caller's existing data fetcher (e.g. getCsvListData)
      // It should return a Promise that resolves to an array of flat objects.
      const data = await fetchData();
      await buildPDF({ title, subtitle, columns, data, fileName, summary });
    } catch (err) {
      console.error("[PdfDownloadButton]", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      icon={<FilePdfOutlined />}
      type="primary"
      danger
      loading={loading}
      onClick={handleClick}
      {...btnProps}
    >
      Download PDF
    </Button>
  );
};

export default PdfDownloadButton;
